import type { ReactNode } from 'react';
import { Input } from 'components/Input';
import {
  CARD_TYPE_IMAGE_ACCEPT_ATTRIBUTE,
} from 'constants/cardTypeMessageTemplates';
import { CARD_TYPE_TAG_COLORS } from 'constants/cardTypeTagColors';
import { cn } from 'utils/cn';
import {
  countGraphemeClusters,
  truncateGraphemeClusters,
} from 'utils/grapheme';
import type { TCardTypeTagColor } from 'types/cardTypeMessage';

type CharCountInputProps = {
  label: string;
  value: string;
  maxLength: number;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  countMode?: 'utf16' | 'grapheme';
  onChange: (value: string) => void;
};

export function CharCountInput({
  label,
  value,
  maxLength,
  placeholder,
  multiline = false,
  rows = 3,
  countMode = 'utf16',
  onChange,
}: CharCountInputProps) {
  const isGraphemeCount = countMode === 'grapheme';
  const displayLength = isGraphemeCount
    ? countGraphemeClusters(value)
    : value.length;
  const truncate = (nextValue: string) =>
    isGraphemeCount
      ? truncateGraphemeClusters(nextValue, maxLength)
      : nextValue.slice(0, maxLength);

  return (
    <label className="grid gap-1.5 text-sm font-semibold text-ink">
      {label}
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(truncate(e.target.value))}
          maxLength={isGraphemeCount ? undefined : maxLength}
          placeholder={placeholder}
          rows={rows}
          className="resize-none rounded-xl border border-border bg-white p-3 text-sm font-normal outline-none focus:border-accent"
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(truncate(e.target.value))}
          maxLength={isGraphemeCount ? undefined : maxLength}
          placeholder={placeholder}
          className="font-normal"
        />
      )}
      <span className="text-right text-xs font-normal text-muted">
        {displayLength}/{maxLength}
      </span>
    </label>
  );
}

type ToggleRowProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  children: ReactNode;
};

export function ToggleFormRow({
  checked,
  onChange,
  label,
  children,
}: ToggleRowProps) {
  return (
    <div className="grid gap-3 rounded-xl border border-border/60 bg-[#F9FAFB] p-4">
      <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
        />
        {label}
      </label>
      {checked ? children : null}
    </div>
  );
}

type TagColorPickerProps = {
  value: TCardTypeTagColor;
  onChange: (color: TCardTypeTagColor) => void;
};

export function TagColorPicker({
  value,
  onChange,
}: TagColorPickerProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {CARD_TYPE_TAG_COLORS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          title={opt.label}
          aria-label={opt.label}
          aria-pressed={value === opt.id}
          onClick={() => onChange(opt.id)}
          style={{ backgroundColor: opt.backgroundColor, color: opt.textColor }}
          className={cn(
            'relative h-8 w-8 rounded-full',
            opt.id === 'white' && 'border border-neutral-300',
            value === opt.id && 'ring-2 ring-accent ring-offset-2',
          )}
        >
          {value === opt.id ? (
            <span
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center text-xs"
            >
              ✓
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

type ImageSlotProps = {
  label: string;
  imageUrl?: string;
  onFileSelect: (file: File) => void;
  uploading?: boolean;
};

export function ImageSlot({
  label,
  imageUrl,
  onFileSelect,
  uploading,
}: ImageSlotProps) {
  return (
    <label className="grid gap-2 text-center">
      <div
        className="relative flex aspect-square w-full max-w-[140px] cursor-pointer flex-col items-center justify-center 
      overflow-hidden rounded-xl border-2 border-dashed border-border bg-white hover:border-accent"
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="px-2 text-xs font-medium text-muted">{label}</span>
        )}
        <input
          type="file"
          accept={CARD_TYPE_IMAGE_ACCEPT_ATTRIBUTE}
          className="absolute inset-0 cursor-pointer opacity-0"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onFileSelect(file);
            e.target.value = '';
          }}
        />
      </div>
      {uploading ? (
        <span className="text-[10px] text-muted">アップロード中...</span>
      ) : null}
    </label>
  );
}
